import { api, API } from "@/api/client";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

// Stage 9 Atomic Guard helpers. The generation pipelines return a structured
// HTTP 402 (detail = {message, cost, balance, shortfall}) when coins can't cover
// a compile — normalize it to a friendly string so we never render [object Object].
export function pipelineErr(e, fallback = "Something went wrong") {
  const status = e?.response?.status;
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") {
    if (d.cost != null && d.balance != null) {
      return `Not enough Coins — need ${d.cost}, you have ${d.balance}. Top up ${d.shortfall} more.`;
    }
    return d.message || fallback;
  }
  // Gateway/edge errors (Cloudflare 5xx, empty/HTML origin responses). Never
  // surface the raw Cloudflare error page — it's meaningless and scary to users.
  // These are transient origin/edge hiccups; tell them to retry.
  const raw = e?.response?.data;
  const looksHtml = typeof raw === "string" && /<!DOCTYPE|<html/i.test(raw);
  if ((status && status >= 502 && status <= 524) || looksHtml) {
    return `${fallback} — the server hiccuped (error ${status || "5xx"}). Please try again in a moment.`;
  }
  // A raw transport/timeout message is meaningless to the user — surface the
  // caller's domain-specific fallback instead of a bare "Network Error".
  const msg = e?.message || "";
  if (!d && (/network error/i.test(msg) || /timeout|timed out/i.test(msg) || e?.code === "ERR_NETWORK")) {
    return `${fallback} — connection dropped. Please try again.`;
  }
  // Guard against any other non-JSON/HTML body leaking through.
  if (!d && typeof raw === "string" && raw.length > 160) return `${fallback}. Please try again.`;
  return d || msg || fallback;
}

// Toast the settled Atomic Guard result once a job finishes (charged = deducted
// on success; released = failed so coins returned; free = entitled).
export function guardToast(job) {
  if (!job) return;
  if (job.guard === "charged" && job.cost) {
    toast(`−${job.cost} ${job.cost === 1 ? "Coin" : "Coins"} charged`, { icon: "🪙", duration: 2200 });
  } else if (job.guard === "released") {
    toast("No coins charged — generation didn't complete", { icon: "↩️", duration: 2600 });
  }
  // Nudge any mounted CoinBalanceChip to re-fetch the live spendable balance.
  if (job.guard === "charged" || job.guard === "released") {
    try { window.dispatchEvent(new Event("mist:coins-updated")); } catch { /* noop */ }
  }
}

export const mistApi = {
  actions: () => api.get("/mist-creations/actions").then((r) => r.data),
  socialCopy: (topic, tone) => api.post("/mist-creations/social-copy", { topic, tone }).then((r) => r.data),
  reelCompose: (items) => api.post("/mist-creations/reel/compose", { items }).then((r) => r.data),
  reelFinalize: (payload) => api.post("/mist-creations/reel/finalize", payload).then((r) => r.data),
  reelOutro: () => api.get("/mist-creations/reel/outro").then((r) => r.data),
  // Progressive video timeline (async, timeout-proof) — video reels only
  bakeFrame: (payload) => api.post("/mist-creations/reel/frame/bake", payload).then((r) => r.data),
  reelProjects: () => api.get("/mist-creations/reel/projects").then((r) => r.data),
  reelProject: (id) => api.get(`/mist-creations/reel/project/${id}`).then((r) => r.data),
  reelReorder: (id, order) => api.post(`/mist-creations/reel/project/${id}/reorder`, { order }).then((r) => r.data),
  reelRemoveFrame: (id, assetId) => api.delete(`/mist-creations/reel/project/${id}/frame/${assetId}`).then((r) => r.data),
  reelMerge: (payload) => api.post("/mist-creations/reel/merge", payload).then((r) => r.data),
  reelPublish: (payload) => api.post("/mist-creations/reel/publish", payload).then((r) => r.data),
  // Publish a rendered reel to the public Explore/Reels feed (unhide + stamp meta).
  publishReel: (asset_id, caption = "", title = "") =>
    api.post("/central-core/query_vault", { op: "publish_reel", params: { asset_id, caption, title } }).then((r) => r.data),
  library: () => api.get("/mist-creations/library").then((r) => r.data),
  libraryAssets: (bucket = "images") => api.get(`/mist-creations/library-assets?bucket=${bucket}`).then((r) => r.data),
  libraryAssetDurations: (ids = []) => api.post("/mist-creations/library-asset-durations", { ids }).then((r) => r.data),
  singingCharacter: (payload) => api.post("/pipelines/singing-character", payload).then((r) => r.data),
  singerLockTrack: (payload) => api.post("/pipelines/singer/lock-track", payload).then((r) => r.data),
  v2vRestyle: (payload) => api.post("/pipelines/v2v/submit", payload).then((r) => r.data),
  shareCommunity: (asset_id, genre = "") => api.post("/mist-creations/community/share", { asset_id, genre }).then((r) => r.data),
  unshareCommunity: (asset_id) => api.post("/mist-creations/community/unshare", { asset_id }).then((r) => r.data),
  communitySongs: (genre = "", opts = {}) => {
    const p = new URLSearchParams();
    if (genre && genre !== "ALL") p.set("genre", genre);
    if (opts.trims) p.set("trims", opts.trims);
    if (opts.reel_size) p.set("reel_size", String(opts.reel_size));
    const qs = p.toString();
    return api.get(`/mist-creations/community/songs${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  communityImages: () => api.get("/mist-creations/community/images").then((r) => r.data),
  setSongCover: (asset_id, cover_asset_id) => api.post("/mist-creations/song/cover/set", { asset_id, cover_asset_id }).then((r) => r.data),
  generateSongCover: (asset_id, prompt = "") => api.post("/mist-creations/song/cover/generate", { asset_id, prompt }).then((r) => r.data),
  invoke: (payload) => api.post("/mist-creations/invoke", payload).then((r) => {
    // Fire the coin-burn listener only when the server actually charged (5 MiLink).
    if (r.data?.charge?.charged) window.dispatchEvent(new Event("mist:generated"));
    return r.data;
  }),
  ingest: async (file, track) => {
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    if (track) fd.append("track", track);
    return api.post("/mist-creations/ingest", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  renameAsset: (id, filename) => api.patch(`/mist-creations/asset/${id}`, { filename }).then((r) => r.data),  deleteAsset: (id) => api.delete(`/mist-creations/asset/${id}`).then((r) => r.data),
  trashList: () => api.get(`/mist-creations/trash`).then((r) => r.data),
  restoreAsset: (id) => api.post(`/mist-creations/asset/${id}/restore`).then((r) => r.data),
  purgeAsset: (id) => api.delete(`/mist-creations/trash/${id}`).then((r) => r.data),
  commitAsset: (id, folderId) => api.post(`/mist-creations/asset/${id}/commit`, folderId ? { folder_id: folderId } : {}).then((r) => r.data),
  listVaultFolders: () => api.get(`/mist-creations/folders`).then((r) => r.data),
  createVaultFolder: (name) => api.post(`/mist-creations/folders`, { name }).then((r) => r.data),
  discardAsset: (id) => api.post(`/mist-creations/asset/${id}/discard`).then((r) => r.data),
  communityShare: (asset_id, opts = {}) => api.post(`/mist-creations/community/share`, {
    asset_id,
    genre: opts.genre || "",
    media_category: opts.media_category || "",
    community_folder_id: opts.community_folder_id || "",
  }).then((r) => r.data),
  communityVault: () => api.get(`/mist-creations/community/vault`).then((r) => r.data),
  communityUse: (asset_id) => api.post(`/mist-creations/community/use`, { asset_id }).then((r) => r.data),
  listCommunityFolders: (track = "") => api.get(`/mist-creations/community/folders${track ? `?track=${encodeURIComponent(track)}` : ""}`).then((r) => r.data),
  createCommunityFolder: (name, track = "image") => api.post(`/mist-creations/community/folders`, { name, track }).then((r) => r.data),
  communityAiOrganize: () => api.post(`/mist-creations/community/ai_organize`).then((r) => r.data),
  communityRename: (asset_id, name) => api.post(`/mist-creations/community/admin/rename`, { asset_id, name }).then((r) => r.data),
  communityDelete: (asset_id) => api.post(`/mist-creations/community/admin/delete`, { asset_id }).then((r) => r.data),
  communityMove: (asset_id, folder_id = "") => api.post(`/mist-creations/community/admin/move`, { asset_id, folder_id }).then((r) => r.data),
  communityGenerateCover: (asset_id) => api.post(`/mist-creations/community/admin/generate-cover`, { asset_id }).then((r) => r.data),
  albums: () => api.get("/mist-creations/albums").then((r) => r.data),
  autoSort: () => api.post("/mist-creations/auto-sort").then((r) => r.data),
  createAlbum: (name, track) => api.post("/mist-creations/albums", { name, track }).then((r) => r.data),
  updateAlbum: (id, patch) => api.patch(`/mist-creations/albums/${id}`, patch).then((r) => r.data),
  deleteAlbum: (id) => api.delete(`/mist-creations/albums/${id}`).then((r) => r.data),
  // Curated prompt-driven reel templates (ALIX flagship) + per-frame redraw.
  alixTemplate: () => api.get("/templates/alix-reel").then((r) => r.data),
  alixTemplateFrame: (frameIndex) => api.post("/templates/alix-reel/frame", { frameIndex }).then((r) => r.data),
};


// --- Overlay Builder (Phase 1) --------------------------------------------
export const overlaysApi = {
  frameStyles: () => api.get("/overlays/frame-styles").then((r) => r.data),
  generate: (payload) => api.post("/overlays/generate", payload, { timeout: 180000 }).then((r) => r.data),
  track: (payload) => api.post("/overlays/track", payload).then((r) => r.data),
  vault: (kind) => api.get(`/overlays/vault${kind ? `?kind=${kind}` : ""}`).then((r) => r.data),
  remove: (customAssetId) => api.delete(`/overlays/${customAssetId}`).then((r) => r.data),
  cipher: (payload) => api.post("/overlays/cipher", payload, { timeout: 180000 }).then((r) => r.data),
  ciphers: () => api.get("/overlays/ciphers").then((r) => r.data),
  renameCipher: (id, phrase) => api.patch(`/overlays/cipher/${id}`, { phrase }).then((r) => r.data),
};

// Stream a vault asset as an authenticated blob URL (img/audio/video src).
export const streamAsset = async (id) => {
  const res = await api.get(`/central-core/vault-file/${id}`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
};

// Lightweight static first-frame JPEG for a video asset (vault thumbnails).
export const posterAsset = async (id) => {
  const res = await api.get(`/central-core/vault-poster/${id}`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
};

// Tiny 300px WebP grid thumbnail for an IMAGE asset — far smaller than the full
// file, so vault/library grids load fast. Cached immutably by the backend.
export const thumbAsset = async (id) => {
  const res = await api.get(`/central-core/vault-thumb/${id}`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
};

// Fetch a vault asset as a real File — lets a library image feed multipart
// pipelines (e.g. apply-effect) exactly like a device upload.
export const fetchAssetFile = async (id, filename = "vault-image.png") => {
  const res = await api.get(`/central-core/vault-file/${id}`, { responseType: "blob" });
  const blob = res.data;
  return new File([blob], filename, { type: blob.type || "image/png" });
};

// --- Phase 16: native download engine -----------------------------------
const _saveBlob = (blob, filename, ext) => {
  const safe = String(filename || "track").replace(/[^a-z0-9]/gi, "_").toLowerCase() || "track";
  const name = safe.endsWith(`.${ext}`) ? safe : `${safe}.${ext}`;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", name);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Force a native download of an owned vault asset. Fetches bytes over the
// authenticated client as a blob (bypasses webview player redirection) and
// requests the attachment disposition from the backend.
export const downloadAsset = async (id, filename = "track", ext = "mp3") => {
  const res = await api.get(`/central-core/vault-file/${id}`, {
    responseType: "blob",
    params: { download: 1 },
  });
  _saveBlob(res.data, filename, ext);
};

// Download from a direct/public media URL (e.g. Mistbeats audio_url).
export const downloadFromUrl = async (audioUrl, filename = "track", ext = "mp3") => {
  const response = await fetch(audioUrl, { method: "GET", mode: "cors" });
  if (!response.ok) throw new Error("download failed");
  _saveBlob(await response.blob(), filename, ext);
};

// Mist Music Studio — two-stage lyric -> master-track workflow.
export const musicApi = {
  lyrics: (payload) => api.post("/music/lyrics", payload).then((r) => r.data),
  lyricsFromImage: (formData) => api.post("/music/lyrics-from-image", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  fixSection: (payload) => api.post("/music/fix-section", payload).then((r) => r.data),
  produce: (payload) => api.post("/music/produce", payload).then((r) => {
    window.dispatchEvent(new Event("mist:generated"));
    return r.data;
  }),
  tracks: () => api.get("/music/tracks").then((r) => r.data),
  track: (id) => api.get(`/music/track/${id}`).then((r) => r.data),
  trim: (asset_id, start, end, title) => api.post("/music/trim", { asset_id, start, end, title }).then((r) => {
    window.dispatchEvent(new Event("mist:generated"));
    return r.data;
  }),
};

// Director Studio V2 — unified scene-array workspace (Cinematic + Comic).
export const directorApi = {
  createProject: (project_name) => api.post("/director/project", { project_name }).then((r) => r.data),
  listProjects: () => api.get("/director/projects").then((r) => r.data),
  getProject: (id) => api.get(`/director/project/${id}`).then((r) => r.data),
  deleteProject: (id) => api.delete(`/director/project/${id}`).then((r) => r.data),
  saveScene: (project_id, scene) => api.post("/director/scene/save", { project_id, scene }).then((r) => r.data),
  generate: (project_id, scene, audio_asset_id) =>
    api.post("/director/generate", { project_id, scene, audio_asset_id: audio_asset_id || "" }).then((r) => r.data),
  job: (job_id) => api.get(`/director/job/${job_id}`).then((r) => r.data),
  export: (project_id) => api.post("/director/export", { project_id }).then((r) => r.data),
  transcribe: (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "audio.webm");
    return api.post("/director/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  aiFix: (text, character_id_lock) =>
    api.post("/director/ai-fix", { text, character_id_lock: character_id_lock || "" }).then((r) => r.data),
  listCast: () => api.get("/director/cast").then((r) => r.data),
  saveCast: (payload) => api.post("/director/cast", payload).then((r) => r.data),
  deleteCast: (id) => api.delete(`/director/cast/${id}`).then((r) => r.data),
  clearCast: () => api.delete("/director/cast").then((r) => r.data),
  // Poll a director job until terminal.
  poll: async (job_id, { interval = 3500, timeout = 360000, onTick } = {}) => {
    const start = Date.now();
    for (;;) {
      let j;
      try { j = await api.get(`/director/job/${job_id}`).then((r) => r.data); } catch { j = null; }
      if (j) { if (onTick) onTick(j); if (j.status === "completed" || j.status === "failed") return j; }
      if (Date.now() - start > timeout) return j || { status: "failed", error: "timeout" };
      await new Promise((res) => setTimeout(res, interval));
    }
  },
};


// Fal.ai creation pipelines (async background jobs) + library counters.
export const pipelinesApi = {
  fixIdentity: async (file, prompt) => {
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    fd.append("prompt", prompt || "");
    return api.post("/pipelines/fix-identity", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  animate: async (file, opts = {}) => {
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    fd.append("motion_prompt", opts.motion_prompt || "");
    fd.append("strength", String(opts.strength ?? 50));
    fd.append("duration", String(opts.duration ?? 4));
    fd.append("audio_preset", opts.audio_preset || "auto");
    return api.post("/pipelines/animate", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  // Upload a locally-recorded game-demo clip; backend transcodes to mp4 + files
  // it into the "Game 6s" vault album, returns { asset_id }.
  gameDemoSave: async (blob, name = "Game Demo", album, stage) => {
    const fd = new FormData();
    const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
    fd.append("file", new File([blob], `game_demo.${ext}`, { type: blob.type || "video/webm" }));
    fd.append("name", name);
    if (album) fd.append("album", album);
    if (stage != null) fd.append("stage", String(stage));
    return api.post("/pipelines/game-demo-save", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  job: (id) => api.get(`/pipelines/job/${id}`).then((r) => r.data),
  // Game Demo project persistence — "Continue Existing Project" workspace.
  gameProjectSave: async ({ name, config, stage, sprites = {}, keepSprites = {}, snapshotDataUrl, stageAssetId }) => {
    const fd = new FormData();
    fd.append("name", name);
    fd.append("config", JSON.stringify(config || {}));
    fd.append("current_stage", String(stage ?? 1));
    fd.append("keep_sprites", JSON.stringify(keepSprites || {}));
    if (stageAssetId) fd.append("stage_asset_id", stageAssetId);
    for (const [slot, file] of Object.entries(sprites)) {
      if (file) fd.append(`sprite_${slot}`, file, `${slot}.png`);
    }
    if (snapshotDataUrl) {
      try { const b = await (await fetch(snapshotDataUrl)).blob(); fd.append("snapshot", new File([b], "snapshot.jpg", { type: "image/jpeg" })); } catch { /* noop */ }
    }
    return api.post("/pipelines/game-project/save", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  gameProjectList: () => api.get("/pipelines/game-project/list").then((r) => r.data),
  gameProjectLoad: (name) => api.post("/pipelines/game-project/load", { name }).then((r) => r.data),
  gameProjectDelete: (name) => api.delete(`/pipelines/game-project/${encodeURIComponent(name)}`).then((r) => r.data),
  faceSwap: async (targetFile, refFile, style, restore) => {
    const fd = new FormData();
    fd.append("target", await compressImage(targetFile));
    fd.append("reference", await compressImage(refFile));
    fd.append("style", style || "");
    fd.append("restore", restore || "crisp");
    return api.post("/pipelines/face-swap", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  detectFaces: async (targetFile) => {
    const fd = new FormData();
    fd.append("target", await compressImage(targetFile));
    return api.post("/pipelines/detect-faces", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  multiFaceSwap: async (targetFile, routes, sourceMap, restore) => {
    // routes = [{index, box:{x,y,w,h}}]; sourceMap = { [index]: File }
    const fd = new FormData();
    fd.append("target", await compressImage(targetFile));
    fd.append("routes", JSON.stringify(routes));
    fd.append("restore", restore || "crisp");
    for (const r of routes) { if (sourceMap[r.index]) fd.append(`source_${r.index}`, await compressImage(sourceMap[r.index])); }
    return api.post("/pipelines/multi-face-swap", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  stitch: (action, input_file_url, overlay_file_url) =>
    api.post("/pipelines/stitch", { action, input_file_url, overlay_file_url }).then((r) => r.data),
  musicProduce: (payload) =>
    api.post("/pipelines/music-produce", payload).then((r) => r.data),
  albumCover: (prompt) =>
    api.post("/pipelines/album-cover", { prompt }).then((r) => r.data),
  enhanceCover: (asset_id) =>
    api.post("/pipelines/enhance-cover", { asset_id }).then((r) => r.data),
  outroRender: (payload) => api.post("/pipelines/outro/render", payload).then((r) => r.data),
  outroList: () => api.get("/pipelines/outro/list").then((r) => r.data),
  outroSetDefault: (asset_id) => api.post("/pipelines/outro/default", { asset_id }).then((r) => r.data),
  outroSpec: (id) => api.get(`/pipelines/outro/spec/${id}`).then((r) => r.data),
  outroDuplicate: (id) => api.post(`/pipelines/outro/duplicate/${id}`).then((r) => r.data),
  outroRename: (id, name) => api.post(`/pipelines/outro/rename/${id}`, { name }).then((r) => r.data),
  outroDelete: (id) => api.delete(`/pipelines/outro/${id}`).then((r) => r.data),
  confirmVault: (id) => api.get(`/pipelines/vault/confirm/${id}`).then((r) => r.data),
  scenesList: () => api.get("/pipelines/scenes/list").then((r) => r.data),
  generateAd: (payload) => api.post("/pipelines/generate-ad", payload).then((r) => r.data),
  businessProfile: () => api.get("/pipelines/business-profile").then((r) => r.data),
  adMakerAccess: () => api.get("/pipelines/ad-maker-access").then((r) => r.data),
  publishAdVariant: (payload) => api.post("/pipelines/publish-ad-variant", payload).then((r) => r.data),
  musicVideo: (payload) =>
    api.post("/pipelines/music-video", payload).then((r) => r.data),
  musicVideo60: (payload) =>
    api.post("/pipelines/music-video-60", payload).then((r) => r.data),
  verifyVideoKey: (api_key) =>
    api.post("/pipelines/verify-video-key", { api_key }).then((r) => r.data),
  videoKeyStatus: () =>
    api.get("/pipelines/video-key-status").then((r) => r.data),
  poemVideo: (bg_file_url, poem_text, mode) =>
    api.post("/pipelines/poem-video", { bg_file_url, poem_text, mode }).then((r) => r.data),
  applyEffect: async (file, effect) => {
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    fd.append("effect", effect);
    return api.post("/pipelines/apply-effect", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  poemBackdrop: (prompt) =>
    api.post("/pipelines/poem-backdrop", { prompt }).then((r) => r.data),
  generateImage: (prompt, style, referenceAssetIds, aspect) =>
    api.post("/pipelines/generate-image", { prompt, style: style || null, aspect: aspect || "9:16", reference_asset_ids: referenceAssetIds && referenceAssetIds.length ? referenceAssetIds : null }).then((r) => r.data),
  nextScene: (seed_asset_id, scene, adherence, style) =>
    api.post("/pipelines/next-scene", { seed_asset_id, scene, adherence: adherence || "balanced", style: style || null }).then((r) => r.data),
  placeObjects: async (targetFile, objectFiles, markers, instruction) => {
    const fd = new FormData();
    fd.append("target", await compressImage(targetFile));
    for (const f of objectFiles) fd.append("objects", await compressImage(f));
    fd.append("markers", JSON.stringify(markers || []));
    fd.append("instruction", instruction || "");
    return api.post("/pipelines/place-objects", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  uploadSeed: async (file) => {
    const fd = new FormData();
    fd.append("image", await compressImage(file));
    return api.post("/pipelines/upload-seed", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  uploadSeedVideo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/pipelines/upload-seed-video", fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
    }).then((r) => r.data);
  },
  uploadSeedAudio: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/pipelines/upload-seed-audio", fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
    }).then((r) => r.data);
  },
  prepareSongWhisper: (id) => api.post(`/pipelines/audio/${id}/whisper`).then((r) => r.data),
  songWhisperStatus: (id) => api.get(`/pipelines/audio/${id}/whisper-status`).then((r) => r.data),
  chainScene: (payload) => api.post("/pipelines/chain-scene", payload).then((r) => r.data),
  dmSceneGenerate: (payload) => api.post("/pipelines/dm/scene/generate", payload).then((r) => r.data),
  dmBake: (payload) => api.post("/pipelines/dm/bake", payload).then((r) => r.data),
  dmTurnaround: (payload) => api.post("/pipelines/dm/character/turnaround", payload).then((r) => r.data),
  dmScenesParse: (payload) => api.post("/pipelines/dm/scenes/parse", payload).then((r) => r.data),
  dmCaption: (payload) => api.post("/pipelines/dm/caption", payload).then((r) => r.data),
  dmOffload: (payload) => api.post("/pipelines/dm/offload", payload).then((r) => r.data),
  dmReelsPublish: (payload) => api.post("/pipelines/dm/reels/publish", payload).then((r) => r.data),
  dmReelsUnlock: (asset_id) => api.post("/pipelines/dm/reels/unlock", { asset_id }).then((r) => r.data),
  dmReelsTimeline: (payload) => api.post("/pipelines/dm/reels/timeline", payload).then((r) => r.data),
  dmReelsAudioLibrary: () => api.get("/pipelines/dm/reels/audio-library").then((r) => r.data),
  dmUploadMusic: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/pipelines/director/seq/upload-music", fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
    }).then((r) => r.data);
  },
  jobStatus: (jobId) => api.get(`/pipelines/director/job/${jobId}`).then((r) => r.data),
  getSceneBibles: () => api.get("/pipelines/scene-bibles").then((r) => r.data),
  saveSceneBible: (payload) => api.post("/pipelines/scene-bible", payload).then((r) => r.data),
  flattenFrame: (payload) => api.post("/pipelines/flatten-frame", payload).then((r) => r.data),
  stitchChunks: (payload) => api.post("/pipelines/stitch-chunks", payload).then((r) => r.data),
  isolateSubject: (asset_id) =>
    api.post("/pipelines/isolate-subject", { asset_id }).then((r) => r.data),
  lipsync: (payload) => api.post("/pipelines/lipsync", payload).then((r) => r.data),
  tailorPrompt: (payload) =>
    api.post("/pipelines/tailor-prompt", payload).then((r) => r.data),
  // Director Mode — 100% BYOK storyboard→video (Phase 1 draft = free, Phase 2 = 3 Credits).
  directorValidate: () =>
    api.post("/pipelines/director/validate-keys", {}).then((r) => r.data),
  directorBlueprint: (prompt, frameCount, audioBlob, referenceFiles, refStrength) => {
    const fd = new FormData();
    fd.append("prompt", prompt || "");
    fd.append("frame_count", String(frameCount || 3));
    if (audioBlob) fd.append("audio", audioBlob, "concept.webm");
    // Up to 5 reference images (multi-field). Accepts a single File or an array.
    const refs = Array.isArray(referenceFiles) ? referenceFiles : (referenceFiles ? [referenceFiles] : []);
    refs.slice(0, 5).forEach((f, i) => { if (f) fd.append("references", f, f.name || `reference_${i + 1}.png`); });
    if (refStrength != null) fd.append("ref_strength", String(refStrength));
    return api.post("/pipelines/director/blueprint", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  directorSaveStoryboard: (payload) =>
    api.post("/pipelines/director/storyboard/save", payload).then((r) => r.data),
  directorStoryboards: () =>
    api.get("/pipelines/director/storyboards").then((r) => r.data),
  directorLoadStoryboard: (sid) =>
    api.get(`/pipelines/director/storyboard/${sid}`).then((r) => r.data),
  directorDeleteStoryboard: (sid) =>
    api.delete(`/pipelines/director/storyboard/${sid}`).then((r) => r.data),
  directorCancel: (jobId) =>
    api.post(`/pipelines/director/cancel/${jobId}`, {}).then((r) => r.data),
  directorMusicTracks: () =>
    api.get("/pipelines/director/music-tracks").then((r) => r.data),
  directorScore: (videoAssetId, trackId) =>
    api.post("/pipelines/director/score", { video_asset_id: videoAssetId, track_id: trackId }).then((r) => r.data),
  directorGenerate: (frames, frameCount) =>
    api.post("/pipelines/director/generate", { frames, frame_count: frameCount }).then((r) => r.data),
  // Sequential Director — 6-stage pipeline with terminal-frame continuity bridge.
  seqUploadCharacter: (file) => {
    const fd = new FormData();
    fd.append("file", file, file.name || "character.png");
    return api.post("/pipelines/director/seq/upload-character", fd,
      { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  seqUploadMusic: (file) => {
    const fd = new FormData();
    fd.append("file", file, file.name || "track.mp3");
    return api.post("/pipelines/director/seq/upload-music", fd,
      { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  seqCreate: (payload) =>
    api.post("/pipelines/director/seq/create", payload).then((r) => r.data),
  seqGet: (sid) =>
    api.get(`/pipelines/director/seq/${sid}`).then((r) => r.data),
  seqSceneGenerate: (sid, index, prompt) =>
    api.post(`/pipelines/director/seq/${sid}/scene/${index}/generate`,
      (prompt || "").trim() ? { prompt } : {}).then((r) => r.data),
  seqSceneApprove: (sid, index) =>
    api.post(`/pipelines/director/seq/${sid}/scene/${index}/approve`, {}).then((r) => r.data),
  seqStitch: (sid) =>
    api.post(`/pipelines/director/seq/${sid}/stitch`, {}).then((r) => r.data),
  // R-System Pre-Production Director — multi-angle reference asset locking.
  rsCreate: (payload) =>
    api.post("/pipelines/director/rsystem/create", payload).then((r) => r.data),
  rsList: () =>
    api.get("/pipelines/director/rsystem").then((r) => r.data),
  rsGet: (pid) =>
    api.get(`/pipelines/director/rsystem/${pid}`).then((r) => r.data),
  rsUpdate: (pid, payload) =>
    api.patch(`/pipelines/director/rsystem/${pid}`, payload).then((r) => r.data),
  rsSetWeight: (pid, value) =>
    api.post(`/pipelines/director/rsystem/${pid}/weight`, { value }).then((r) => r.data),
  rsDelete: (pid) =>
    api.delete(`/pipelines/director/rsystem/${pid}`).then((r) => r.data),
  rsSlotGenerate: (pid, code, prompt) =>
    api.post(`/pipelines/director/rsystem/${pid}/slot/${code}/generate`,
      (prompt || "").trim() ? { prompt } : {}).then((r) => r.data),
  rsSlotUpload: (pid, code, file) => {
    const fd = new FormData();
    fd.append("file", file, file.name || "reference.png");
    return api.post(`/pipelines/director/rsystem/${pid}/slot/${code}/upload`, fd,
      { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  rsSlotSetAsset: (pid, code, assetId) =>
    api.post(`/pipelines/director/rsystem/${pid}/slot/${code}/set-asset`, { asset_id: assetId }).then((r) => r.data),
  rsSlotLock: (pid, code) =>
    api.post(`/pipelines/director/rsystem/${pid}/slot/${code}/lock`, {}).then((r) => r.data),
  rsSlotUnlock: (pid, code) =>
    api.post(`/pipelines/director/rsystem/${pid}/slot/${code}/unlock`, {}).then((r) => r.data),
  rsSlotClear: (pid, code) =>
    api.post(`/pipelines/director/rsystem/${pid}/slot/${code}/clear`, {}).then((r) => r.data),
  rsSaveTimeline: (pid, shots) =>
    api.post(`/pipelines/director/rsystem/${pid}/timeline`, { shots }).then((r) => r.data),
  rsRenderFilm: (pid) =>
    api.post(`/pipelines/director/rsystem/${pid}/render`, {}).then((r) => r.data),
  // O1 — SSE-first job tracking: opens an EventSource (near-zero DB polling; instant
  // updates), and transparently falls back to directorPoll if SSE is unavailable or
  // the connection drops. Same options/return shape as directorPoll.
  directorStream: (id, { onTick, shouldStop, onNetHiccup, timeout = 1500000 } = {}) =>
    new Promise((resolve, reject) => {
      let settled = false, es = null, fellBack = false, stopChk = null, hardTimer = null;
      const cleanup = () => { try { es && es.close(); } catch { /* noop */ } if (stopChk) clearInterval(stopChk); if (hardTimer) clearTimeout(hardTimer); };
      const done = (j) => { if (settled) return; settled = true; cleanup(); resolve(j); };
      const fail = (e) => { if (settled) return; settled = true; cleanup(); reject(e); };
      const fallback = () => {
        if (fellBack || settled) return; fellBack = true;
        try { es && es.close(); } catch { /* noop */ }
        if (stopChk) clearInterval(stopChk);
        pipelinesApi.directorPoll(id, { onTick, shouldStop, onNetHiccup, timeout }).then(done).catch(fail);
      };
      if (typeof EventSource === "undefined") { fallback(); return; }
      const wireStream = (ticket) => {
        if (settled || fellBack) return;
        const url = `${API}/pipelines/director/job/${id}/stream?ticket=${encodeURIComponent(ticket)}`;
        try { es = new EventSource(url); } catch { fallback(); return; }
        stopChk = setInterval(() => { if (shouldStop && shouldStop()) done({ status: "cancelled" }); }, 1000);
        es.onmessage = (ev) => {
          if (!ev.data) return;
          let j; try { j = JSON.parse(ev.data); } catch { return; }
          if (onNetHiccup) { try { onNetHiccup(0); } catch { /* noop */ } }
          if (onTick) { try { onTick(j); } catch { /* noop */ } }
          if (j.status && j.status !== "processing") done(j);
        };
        es.onerror = () => {
          if (onNetHiccup) { try { onNetHiccup(1); } catch { /* noop */ } }
          // readyState 2 = CLOSED and won't auto-reconnect → degrade to polling.
          if (es && es.readyState === 2) fallback();
        };
      };
      // C4 — mint a short-lived one-time stream ticket (never put the JWT in the
      // URL). On any hiccup getting the ticket, degrade to polling.
      api.post("/pipelines/stream-ticket", {})
        .then((r) => {
          const ticket = r && r.data && r.data.ticket;
          if (!ticket) { fallback(); return; }
          wireStream(ticket);
        })
        .catch(() => fallback());
      hardTimer = setTimeout(() => { if (!settled) fallback(); }, timeout);
    }),
  directorPoll: async (id, { interval = 6000, timeout = 1500000, onTick, shouldStop, onNetHiccup, maxRetries = 8 } = {}) => {
    const start = Date.now();
    let netFails = 0;
    while (Date.now() - start < timeout) {
      if (shouldStop && shouldStop()) return { status: "cancelled" };
      try {
        const j = await api.get(`/pipelines/director/job/${id}`).then((r) => r.data);
        if (netFails > 0 && onNetHiccup) { try { onNetHiccup(0); } catch { /* noop */ } } // recovered
        netFails = 0;
        if (onTick) { try { onTick(j); } catch { /* noop */ } }
        if (j.status && j.status !== "processing") return j;
        await new Promise((r) => setTimeout(r, interval));
      } catch (e) {
        netFails += 1;
        if (netFails >= maxRetries) throw e;
        // Progressive backoff — staggered reconnect starting ~3s, capped at 15s.
        if (onNetHiccup) { try { onNetHiccup(netFails); } catch { /* noop */ } }
        const backoff = Math.min(3000 * Math.pow(2, netFails - 1), 15000);
        await new Promise((r) => setTimeout(r, backoff));
      }
    }
    throw new Error("Job timed out");
  },
  // Mist Director — ONE unified async render (frames+overlays+fades → outro/music/url).
  reelRender: (payload) =>
    api.post("/pipelines/reel-render", payload).then((r) => r.data),
  // Music Visualizer — still image + Mist song → beat-synced sound-wave reel.
  musicVisualizer: (payload) =>
    api.post("/pipelines/music-visualizer", payload).then((r) => r.data),
  // Vibe Prompting — describe it, AI builds the whole music video.
  musicVizVibe: (payload) =>
    api.post("/pipelines/music-viz-vibe", payload).then((r) => r.data),
  // Site-wide render control — floating "Rendering…" dock (list + Cancel / Cancel all).
  activeJobs: () => api.get("/pipelines/active-jobs").then((r) => r.data),
  cancelJob: (jobId) => api.post(`/pipelines/job/${jobId}/cancel`, {}).then((r) => r.data),
  cancelAllJobs: () => api.post("/pipelines/jobs/cancel-all", {}).then((r) => r.data),
  // Music Viz — does this Library song have lyrics for the scroll crawl?
  songLyrics: (assetId) =>
    api.get(`/pipelines/song-lyrics/${assetId}`).then((r) => r.data),
  // Mist Director — lyric-free thematic background music (30s / 60s).
  instrumentalMusic: (payload) =>
    api.post("/pipelines/instrumental-music", payload).then((r) => r.data),
  // Mist Create — Overlay Removal (LaMa clean-plate). Pass a device file OR a vault filename.
  imageStrip: async (file, vaultFilename) => {
    const fd = new FormData();
    if (file) fd.append("file", await compressImage(file));
    if (vaultFilename) fd.append("vault_filename", vaultFilename);
    return api.post("/pipelines/image-strip", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  // Mist Create — Reel/Video Overlay Removal (fal-ai/void-video-inpainting clean plate).
  videoStrip: (file, vaultFilename) => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    if (vaultFilename) fd.append("vault_filename", vaultFilename);
    return api.post("/pipelines/video-strip", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  // Poll a job until it leaves the "processing" state (or times out). Transient
  // failures — a dropped connection OR a gateway/Cloudflare 5xx/520-524 while the
  // origin is briefly busy mid-render — are tolerated: we keep polling (with
  // backoff) until the overall timeout, because the render is still running
  // server-side and will complete. Previously a short Cloudflare 520 burst made
  // the poll give up, so a FINISHED reel surfaced as an error and the user had to
  // hard-refresh (losing in-progress work). Only a real client error (4xx other
  // than 429) is fatal and surfaces immediately. `onNetHiccup(n)` reports
  // reconnect attempts (0 = recovered).
  poll: async (id, { interval = 5000, timeout = 300000, onNetHiccup, onTick } = {}) => {
    const start = Date.now();
    let netFails = 0;
    const isTransient = (e) => {
      const s = e?.response?.status;
      if (s === undefined || s === null) return true;   // network drop / edge reset
      if (s === 429) return true;                        // throttle
      return s >= 500;                                   // 502/503/504 + Cloudflare 520-527
    };
    while (Date.now() - start < timeout) {
      try {
        const j = await api.get(`/pipelines/job/${id}`).then((r) => r.data);
        if (netFails > 0 && onNetHiccup) { try { onNetHiccup(0); } catch { /* noop */ } }
        netFails = 0;
        if (onTick) { try { onTick(j); } catch { /* noop */ } }
        if (j.status && j.status !== "processing") return j;
        await new Promise((r) => setTimeout(r, interval));
      } catch (e) {
        if (!isTransient(e)) throw e;    // genuine 4xx (404/403…) is fatal
        netFails += 1;
        if (onNetHiccup) { try { onNetHiccup(netFails); } catch { /* noop */ } }
        const backoff = Math.min(3000 * Math.pow(2, Math.min(netFails, 3)), 12000);
        await new Promise((r) => setTimeout(r, backoff));
      }
    }
    throw new Error("Job timed out");
  },
};

// Continuity Studio — Consistent Imaging Reel Maker (Director Continuity Mode).
// Conforms a target scene to reference scene(s) so a set of images share one look.
export const continuityApi = {
  conform: (payload) =>
    api.post("/continuity/conform", payload, { timeout: 180000 }).then((r) => r.data),
  ingest: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/continuity/ingest", fd, { timeout: 120000 }).then((r) => r.data);
  },
};

export const libraryApi = {
  counts: () => api.get("/library/counts").then((r) => r.data),
  // Save an arbitrary blob into the CURRENT user's personal Mist Library.
  // Available to ALL authenticated users (unlike founder-gated Stock).
  saveBlob: ({ file, name, track = "IMAGES" }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    fd.append("track", track);
    return api.post("/mist-creations/save-to-library", fd).then((r) => r.data);
  },
};

// Mist Create — Prompt Folder (personal + community reusable prompts).
export const promptsApi = {
  list: (scope = "personal", q = "") =>
    api.get(`/mist-creations/prompts?scope=${scope}${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => r.data),
  save: (payload) => api.post("/mist-creations/prompts", payload).then((r) => r.data),
  remove: (id) => api.delete(`/mist-creations/prompts/${id}`).then((r) => r.data),
  parse: (prompt_text) => api.post("/mist-creations/prompts/parse", { prompt_text }).then((r) => r.data),
};

// Mist Video Editor (Phase 1) — $0 local FFmpeg + OpenCV pipeline.
export const editorApi = {
  videos: () => api.get("/editor/videos").then((r) => r.data),
  ingestDevice: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/pipelines/upload-seed-video", fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
    }).then((r) => r.data);
  },
  saveFrame: (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "frame.png");
    return api.post("/editor/save-frame", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  trim: (asset_id, start, end) =>
    api.post("/editor/trim", { asset_id, start, end }).then((r) => r.data),
  audio: () => api.get("/editor/audio").then((r) => r.data),
  stylize: (asset_id, opts = {}) =>
    api.post("/editor/stylize", { asset_id, ...opts }).then((r) => r.data),
  job: (id) => api.get(`/editor/job/${id}`).then((r) => r.data),
  postToFeed: (asset_id, caption, visibility = "public") =>
    api.post("/editor/post-to-feed", { asset_id, caption, visibility }).then((r) => r.data),
  aiCaption: (style) => api.post("/editor/ai-caption", { style }).then((r) => r.data),
};

export const ballSnipeApi = {
  state: () => api.get("/games/ball-snipe/state").then((r) => r.data),
  adReward: (level, context = "level_gate") =>
    api.post("/games/ball-snipe/ad-reward", { level, context }).then((r) => r.data),
  claimReward: (feature) =>
    api.post("/games/ball-snipe/claim-reward", { feature }).then((r) => r.data),
  qr: () => api.get(`/games/ball-snipe/qr?origin=${encodeURIComponent(window.location.origin)}`).then((r) => r.data),
  rewardNow: () => api.post("/games/ball-snipe/reward-now").then((r) => r.data),
  collect: (level, game = "ball_snipe", events = 1) =>
    api.post("/games/ball-snipe/collect", { level, game, events }).then((r) => r.data),
};

// Phase 2 — Single-Scene Project Vault (silent per-scene clips, LTX-2.3 @1080p).
export const projectsApi = {
  list: () => api.get("/projects").then((r) => r.data),
  create: (title) => api.post("/projects", { title: title || "" }).then((r) => r.data),
  get: (pid) => api.get(`/projects/${pid}`).then((r) => r.data),
  remove: (pid) => api.delete(`/projects/${pid}`).then((r) => r.data),
  addScene: async (pid, { mode, motion_prompt, brief, duration, image_asset_id, file }) => {
    const fd = new FormData();
    fd.append("mode", mode || "brief");
    fd.append("motion_prompt", motion_prompt || "");
    fd.append("brief", brief || "");
    fd.append("duration", String(duration || 6));
    if (image_asset_id) fd.append("image_asset_id", image_asset_id);
    if (file) { const f = await compressImage(file); fd.append("file", f, f.name || "image.png"); }
    return api.post(`/projects/${pid}/scene`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  rerenderScene: async (pid, sid, { motion_prompt, brief, duration, image_asset_id, file } = {}) => {
    const fd = new FormData();
    if (motion_prompt != null) fd.append("motion_prompt", motion_prompt);
    if (brief != null) fd.append("brief", brief);
    if (duration != null) fd.append("duration", String(duration));
    if (image_asset_id) fd.append("image_asset_id", image_asset_id);
    if (file) { const f = await compressImage(file); fd.append("file", f, f.name || "image.png"); }
    return api.post(`/projects/${pid}/scene/${sid}/rerender`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  importScene: (pid, file, duration) => {
    const fd = new FormData();
    fd.append("file", file, file.name || "clip.mp4");
    if (duration != null) fd.append("duration", String(duration));
    return api.post(`/projects/${pid}/scene/import`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  editScene: (pid, sid, patch) => api.patch(`/projects/${pid}/scene/${sid}`, patch).then((r) => r.data),
  deleteScene: (pid, sid) => api.delete(`/projects/${pid}/scene/${sid}`).then((r) => r.data),
  compile: (pid, { title, overlays, music, voiceover, outro, outro_asset_id } = {}) => {
    const fd = new FormData();
    fd.append("title", title || "");
    fd.append("overlays", JSON.stringify(overlays || {}));
    if (outro_asset_id) fd.append("outro_asset_id", outro_asset_id);
    if (music) fd.append("music", music, music.name || "music.mp3");
    if (voiceover) fd.append("voiceover", voiceover, voiceover.name || "voice.mp3");
    if (outro) fd.append("outro", outro, outro.name || "outro.mp4");
    return api.post(`/projects/${pid}/compile`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  generateOutro: () => api.post("/projects/outro").then((r) => r.data),
};

// Alix Studio Core — multimodal vision + creative orchestration.
export const alixApi = {
  vision: async (file, prompt) => {
    const fd = new FormData();
    fd.append("image", await compressImage(file));
    fd.append("prompt", prompt || "");
    return api.post("/alix/vision", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  generateCreative: (mode, input_context) =>
    api.post("/alix/generate-creative", { mode, input_context }).then((r) => r.data),
};

// Founder-only API Credit Ledger — pay-as-you-go balances + burn-rate alerts.
export const ledgerApi = {
  list: () => api.get("/ledger").then((r) => r.data),
  alerts: () => api.get("/ledger/alerts").then((r) => r.data),
  upsert: (payload) => api.post("/ledger/provider", payload).then((r) => r.data),
  topup: (provider, amount) => api.post("/ledger/topup", { provider, amount }).then((r) => r.data),
  remove: (provider) => api.delete(`/ledger/provider/${provider}`).then((r) => r.data),
};

// Voice Studio — AI Voice Lab, Recording Crib, Voice Vault Archive.
export const voiceApi = {
  voices: () => api.get("/voice/voices").then((r) => r.data),
  synthesize: (payload) => api.post("/voice/synthesize", payload).then((r) => {
    window.dispatchEvent(new Event("mist:generated"));
    return r.data;
  }),
  s2sAccents: () => api.get("/voice/accents").then((r) => r.data),
  s2sMorph: (formData) => api.post("/voice/s2s_accent_morph", formData, {
    headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
  }).then((r) => {
    window.dispatchEvent(new Event("mist:generated"));
    return r.data;
  }),
  cribIngest: (file, duration, label) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("duration", String(duration ?? 0));
    fd.append("label", label || "");
    return api.post("/voice/crib/ingest", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  vault: () => api.get("/voice/vault").then((r) => r.data),
  postToReel: (payload) => api.post("/voice/post-to-reel", payload).then((r) => r.data),
  cloneAndTest: (formData) => api.post("/audio/voice-lab/clone-and-test", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data),
  clonedVoices: () => api.get("/voice/cloned").then((r) => r.data),
  renameCloned: (voiceId, name) => api.patch(`/voice/cloned/${voiceId}`, { name }).then((r) => r.data),
  deleteCloned: (voiceId) => api.delete(`/voice/cloned/${voiceId}`).then((r) => r.data),
  poemAudio: (poemId) => api.post(`/voice/poem-audio/${poemId}`).then((r) => r.data),
  transcribe: (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "dictation.webm");
    return api.post("/vault-security/blueprint/transcribe", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
};

// Admin System Panel (founder-only root operations).
export const adminApi = {
  users: () => api.get("/admin/users").then((r) => r.data),
  userAction: (uid, action) => api.post(`/admin/users/${uid}/action`, { action }).then((r) => r.data),
  apiBudget: () => api.get("/admin/api-budget").then((r) => r.data),
  dbClean: (target) => api.post("/admin/db/clean", { target }).then((r) => r.data),
  logs: (lines = 60) => api.get(`/admin/logs?lines=${lines}`).then((r) => r.data),
  healthChecks: () => api.get("/admin/health-checks").then((r) => r.data),
  errorScan: (lines = 400) => api.get(`/admin/error-scan?lines=${lines}`).then((r) => r.data),
  generationFailures: (limit = 50) => api.get(`/admin/generation-failures?limit=${limit}`).then((r) => r.data),
  austracDashboard: () => api.get("/admin/austrac").then((r) => r.data),
  austracToggle: (live) => api.post("/admin/austrac/toggle", { live }).then((r) => r.data),
  bankStatus: () => api.get("/founder/bank/status").then((r) => r.data),
  bankConnect: (mobile) => api.post("/founder/bank/connect", { mobile }).then((r) => r.data),
  bankSync: (jobId) => api.post("/founder/scrape-sync", { jobId }).then((r) => r.data),
  exportCompliancePdf: () => api.get("/admin/compliance/export-pdf", { responseType: "blob" }).then((r) => r.data),
};

export const adsApi = {
  list: () => api.get("/ads/slots").then((r) => r.data),
  create: (formData) => api.post("/ads/slots", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  toggle: (id, active) => api.post(`/ads/slots/${id}/toggle`, { active }).then((r) => r.data),
  remove: (id) => api.delete(`/ads/slots/${id}`).then((r) => r.data),
  serve: (scope = "superapp") => api.get(`/ads/serve?scope=${scope}`).then((r) => r.data),
  serveGame: () => api.get("/ads/serve?scope=game").then((r) => r.data),
  click: (id, scope = "superapp") => api.post(`/ads/click/${id}?scope=${scope}`).then((r) => r.data),
  analytics: (days = 14) => api.get(`/ads/analytics?days=${days}`).then((r) => r.data),
  packages: () => api.get("/ads/packages").then((r) => r.data),
  submit: (formData) => api.post("/ads/submit", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  mine: () => api.get("/ads/mine").then((r) => r.data),
  editMine: (id, body) => api.post(`/ads/mine/${id}/edit`, body).then((r) => r.data),
  pending: () => api.get("/ads/pending").then((r) => r.data),
  review: (id, decision, reason = "") => api.post(`/ads/${id}/review`, { decision, reason }).then((r) => r.data),
  checkout: (id, originUrl) => api.post(`/ads/${id}/checkout`, { origin_url: originUrl }).then((r) => r.data),
  checkoutStatus: (sid) => api.get(`/ads/checkout/status/${sid}`).then((r) => r.data),
  mediaUrl: (slotId, download = false) => `${API}/ads/media/${slotId}${download ? "?download=1" : ""}`,
  generatePromoReel: (link) => api.post("/ads/promo-reel/generate", { link }).then((r) => r.data),
};
